/**
 * API Adapter — convierte el catálogo público de ChekList en los modelos internos.
 *
 * Origen: GET {CHECKLIST}/api/publico/catalogo
 * Respuesta:
 *   {
 *     productos: [{ id, nombre, categoria, descripcion, precioUsd, precioCup,
 *                   unidad, stock, imagen }],
 *     contacto: { whatsapp, email },
 *     vendedores: [{ codigo, nombre, whatsapp }]
 *   }
 *
 * Los datos salen de las pestañas Tienda e Inventario del Google Sheet que
 * administra ChekList. Las fotos se sirven en {CHECKLIST}/api/publico/foto/{imagen}.
 */

import type { Product } from '@/types';
import { slugCategoria, type ProductoSeo } from '@/seo/datos';
import { API_BASE_URL } from './api';

// ─── Forma cruda de la API ────────────────────────────────────────────────────

export interface RawProducto {
  id: string;
  nombre: string;
  categoria: string;
  descripcion: string;
  precioUsd: number;
  precioCup: number | null;
  /** Oferta especial activa: precioUsd ya es el de oferta y precioNormalUsd el de siempre */
  enOferta?: boolean;
  precioNormalUsd?: number;
  unidad: string;
  stock: number;
  imagen: string;
}

export interface RawVendedor {
  codigo: string;
  nombre: string;
  whatsapp: string;
  /** Archivo de su foto de perfil, o "" si no subió una */
  foto?: string;
}

/** URL pública de la foto de perfil de un vendedor, o null. */
export function fotoVendedorUrl(v: RawVendedor): string | null {
  return v.foto ? `${API_BASE_URL}/foto/${encodeURIComponent(v.foto)}` : null;
}

export interface RawCatalogo {
  productos?: RawProducto[];
  contacto?: { whatsapp?: string; email?: string };
  vendedores?: RawVendedor[];
}

export interface CategoryGroup {
  categoryId: string;
  categoryName: string;
  products: Product[];
  total: number;
}

// ─── Imágenes ─────────────────────────────────────────────────────────────────

const FALLBACK_IMAGE =
  'https://readdy.ai/api/search-image?query=electrical%20renewable%20energy%20product%20clean%20white%20background%20professional%20ecommerce%20photography%20minimal%20studio%20lighting&width=600&height=600&seq=fallback-product-001&orientation=squarish';

function imageUrl(imagen: string): string {
  return imagen ? `${API_BASE_URL}/foto/${encodeURIComponent(imagen)}` : FALLBACK_IMAGE;
}

// ─── Categorías ───────────────────────────────────────────────────────────────

/** Nombre de la categoría para los productos que no tienen una en el Sheet. */
const SIN_CATEGORIA = 'Otros';

// Misma función que usa el SEO, así las URLs de categoría coinciden siempre.
export const normalizeCategorySlug = slugCategoria;

/** Datos del producto que usa el SEO (título, schema.org Product). */
export function productoSeo(p: Product): ProductoSeo {
  return {
    id: p.id,
    nombre: p.name,
    descripcion: p.description,
    categoria: categoryNameOf(p) === SIN_CATEGORIA ? '' : categoryNameOf(p),
    precioUsd: p.price,
    disponible: p.inStock,
    imagen: p.thumbnail === FALLBACK_IMAGE ? null : p.thumbnail,
  };
}

// ─── Producto ─────────────────────────────────────────────────────────────────

export function adaptProduct(raw: RawProducto): Product {
  const categoryName = raw.categoria?.trim() || SIN_CATEGORIA;
  const description = raw.descripcion?.trim() ?? '';
  const thumbnail = imageUrl(raw.imagen);
  const enOferta =
    !!raw.enOferta && (raw.precioNormalUsd ?? 0) > raw.precioUsd && raw.precioUsd > 0;
  const now = new Date().toISOString();

  return {
    id: raw.id,
    name: raw.nombre,
    slug: raw.id,
    sku: raw.id,
    categoryId: normalizeCategorySlug(categoryName),
    price: raw.precioUsd,
    priceCup: raw.precioCup && raw.precioCup > 0 ? raw.precioCup : undefined,
    // Oferta especial (columnas K y L de la pestaña Tienda): el precio normal
    // queda tachado y el producto aparece en "Ofertas especiales".
    ...(enOferta
      ? {
          originalPrice: raw.precioNormalUsd,
          discount: Math.round((1 - raw.precioUsd / raw.precioNormalUsd!) * 100),
          badge: 'Oferta' as const,
        }
      : {}),
    currency: 'USD',
    thumbnail,
    imageCandidates: [thumbnail],
    images: [{ url: thumbnail, alt: raw.nombre, isPrimary: true }],
    description,
    // Por caracteres reales, para no partir un emoji a la mitad
    shortDescription: Array.from(description).slice(0, 150).join(''),
    features: [],
    specifications: raw.unidad ? [{ label: 'Unidad', value: raw.unidad }] : [],
    rating: 0,
    reviewCount: 0,
    inStock: raw.stock > 0,
    stockQty: raw.stock > 0 ? raw.stock : undefined,
    // El nombre original de la categoría va como tag para recuperarlo al agrupar.
    tags: [categoryName],
    createdAt: now,
    updatedAt: now,
  };
}

export function normalizeProducts(root: unknown): Product[] {
  const productos = (root as RawCatalogo | null)?.productos;
  if (!Array.isArray(productos)) return [];
  return productos.filter((p) => p && p.id && p.nombre).map(adaptProduct);
}

// ─── Agrupar por categoría ────────────────────────────────────────────────────

/** Nombre visible de la categoría de un producto. */
export function categoryNameOf(product: Product): string {
  return (
    product.tags.find((t) => normalizeCategorySlug(t) === product.categoryId) ??
    product.categoryId
  );
}

export function groupProductsByCategory(products: Product[]): CategoryGroup[] {
  const map = new Map<string, CategoryGroup>();

  products.forEach((product) => {
    const existing = map.get(product.categoryId);
    if (existing) {
      existing.products.push(product);
      existing.total += 1;
    } else {
      map.set(product.categoryId, {
        categoryId: product.categoryId,
        categoryName: categoryNameOf(product),
        products: [product],
        total: 1,
      });
    }
  });

  return Array.from(map.values()).sort((a, b) => b.total - a.total);
}
